import { useState, useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    useReactTable,
    getFilteredRowModel,
    ColumnFiltersState,
    getPaginationRowModel,
} from "@tanstack/react-table";
import {
    Plus,
    Search,
    MoreHorizontal,
    Pencil,
    Trash2,
    Loader2,
    Image as ImageIcon,
    Upload,
    AlertTriangle,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
    SheetFooter,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";


// Types
type Product = {
    id: string;
    name: string;
    slug: string;
    description?: string;
    shortDescription?: string;
    type: 'simple' | 'variable';
    status: 'draft' | 'active' | 'inactive' | 'out_of_stock';
    price: number;
    salePrice?: number;
    costPrice?: number;
    stock: number;
    sku?: string;
    barcode?: string;
    weight?: number; // in grams
    images: { url: string; position?: number; isPrimary?: boolean }[]; // API returns objects
    category?: { id: string; name: string };
    categoryId?: string;
    createdAt?: string;
};

type Category = {
    id: string;
    name: string;
};

// Zod Schema
const productFormSchema = z.object({
    name: z.string().min(2, "Name must be at least 2 characters"),
    slug: z.string().optional(), // Auto-generated if empty
    description: z.string().optional(),
    shortDescription: z.string().optional(),
    type: z.enum(['simple', 'variable']).default('simple'),
    status: z.enum(['draft', 'active', 'inactive', 'out_of_stock']).default('draft'),
    price: z.coerce.number().min(0, "Price must be positive"),
    salePrice: z.coerce.number().min(0).optional(),
    costPrice: z.coerce.number().min(0).optional(),
    stock: z.coerce.number().int().min(0).default(0),
    sku: z.string().optional(),
    barcode: z.string().optional(),
    weight: z.coerce.number().min(0).optional(),
    categoryId: z.string().min(1, "Please select a category"),
    images: z.array(z.object({
        value: z.string() // Allow empty string or URL
    })).default([{ value: "" }]),
});

type ProductFormValues = z.infer<typeof productFormSchema>;

export function ProductsPage() {
    const [products, setProducts] = useState<Product[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState(true);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState<Product | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);

    // Form
    const form = useForm({
        resolver: zodResolver(productFormSchema),
        defaultValues: {
            name: "",
            description: "",
            shortDescription: "",
            type: "simple",
            status: "draft",
            sku: "",
            barcode: "",
            price: 0,
            salePrice: 0,
            costPrice: 0,
            stock: 0,
            weight: 0,
            categoryId: "",
            images: [{ value: "" }],
        },
    });

    // Watch product type to show/hide variant tab
    const watchedType = form.watch('type');

    const { fields, append, remove } = useFieldArray({
        name: "images",
        control: form.control,
    });

    // Fetch Data
    const fetchData = async () => {
        setLoading(true);
        try {
            const apiUrl = import.meta.env.VITE_API_URL || '';
            const [prodRes, catRes] = await Promise.all([
                fetch(`${apiUrl}/admin/products`, { credentials: 'include' }),
                fetch(`${apiUrl}/admin/categories`, { credentials: 'include' })
            ]);

            if (prodRes.ok) {
                const data = await prodRes.json();
                setProducts(data.data || []);
            }
            if (catRes.ok) {
                const data = await catRes.json();
                setCategories(data.data || []);
            }
        } catch (error) {
            console.error("Failed to fetch data", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Handlers
    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        setIsUploading(true);
        try {
            // ... existing upload logic unmodified for brevity, implemented below ...
            const formData = new FormData();
            formData.append('folder', 'products');

            // Loop upload
            await Promise.all(Array.from(files).map(async (file) => {
                const fd = new FormData();
                fd.append('file', file);
                fd.append('folder', 'products');

                const apiUrl = import.meta.env.VITE_API_URL || '';
                const res = await fetch(`${apiUrl}/admin/upload/proxy`, {
                    method: 'POST',
                    credentials: 'include',
                    body: fd,
                });

                if (!res.ok) throw new Error('Upload failed');
                const data = await res.json();

                // If the first image was empty, replace it
                const currentImages = form.getValues('images') as { value: string }[];
                if (currentImages.length === 1 && currentImages[0]?.value === "") {
                    form.setValue('images', [{ value: data.data.publicUrl }]);
                } else {
                    append({ value: data.data.publicUrl });
                }
            }));

        } catch (error: any) {
            console.error("Upload failed", error);
            setUploadError(error.message || "Upload failed. Please check your R2 configuration.");
        } finally {
            setIsUploading(false);
            if (e.target) e.target.value = '';
        }
    };

    const onSubmit = async (data: ProductFormValues) => {
        console.log("onSubmit triggered with data:", data);
        try {
            const rawSlug = data.slug || data.sku?.trim() || data.name.trim();
            const slug = rawSlug.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

            // Transform images from { value: string } to { url: string, position: number }
            const formattedImages = data.images
                .filter(img => img.value && img.value.trim() !== '')
                .map((img, index) => ({
                    url: img.value,
                    position: index,
                    isPrimary: index === 0,
                }));

            const payload = {
                name: data.name,
                slug,
                description: data.description || null,
                shortDescription: data.shortDescription || null,
                type: data.type,
                status: data.status,
                price: data.price,
                salePrice: data.salePrice || null,
                costPrice: data.costPrice || null,
                stock: data.stock,
                sku: data.sku || null,
                barcode: data.barcode || null,
                weight: data.weight || null,
                categoryId: data.categoryId || null,
                images: formattedImages,
            };

            console.log('[Product Save] Payload:', JSON.stringify(payload, null, 2));

            const apiUrl = import.meta.env.VITE_API_URL || '';
            const url = editingProduct
                ? `${apiUrl}/admin/products/${editingProduct.id}`
                : `${apiUrl}/admin/products`;

            const method = editingProduct ? "PUT" : "POST";

            const res = await fetch(url, {
                method,
                credentials: 'include',
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                console.error('[Product Save] API Error:', errData);
                throw new Error(errData.message || "Failed to save product");
            }

            await fetchData();
            setIsDialogOpen(false);
            form.reset();
            setEditingProduct(null);
        } catch (error: any) {
            console.error('[Product Save] Error:', error);
            alert(error.message || "Failed to save product");
        }
    };

    // Debug form validation errors
    const onFormError = (errors: any) => {
        console.error('[Form Validation Errors]:', errors);
        alert('Form validation failed. Check console for details.');
    };

    const handleDelete = async (id: string) => {
        if (!confirm("Are you sure you want to delete this product?")) return;
        try {
            const apiUrl = import.meta.env.VITE_API_URL || '';
            const res = await fetch(`${apiUrl}/admin/products/${id}`, { method: "DELETE", credentials: 'include' });
            if (!res.ok) throw new Error("Failed to delete");
            await fetchData();
        } catch (error) {
            console.error(error);
        }
    };

    const handleEdit = (product: Product) => {
        setEditingProduct(product);

        // Handle images - API returns { url, position } objects, form uses { value } format
        let formImages: { value: string }[] = [{ value: "" }];
        if (product.images && product.images.length > 0) {
            formImages = product.images.map((img: string | { url: string }) => {
                // Handle both string URLs and object format
                const url = typeof img === 'string' ? img : img.url;
                return { value: url };
            });
        }

        form.reset({
            name: product.name,
            description: product.description || "",
            shortDescription: product.shortDescription || "",
            type: product.type || "simple",
            status: product.status || "draft",
            sku: product.sku || "",
            barcode: product.barcode || "",
            price: product.price,
            salePrice: product.salePrice || 0,
            costPrice: product.costPrice || 0,
            stock: product.stock,
            weight: product.weight || 0,
            categoryId: product.categoryId || product.category?.id || "",
            images: formImages,
        });
        setIsDialogOpen(true);
    };

    const handleAddNew = () => {
        setEditingProduct(null);
        form.reset({
            name: "",
            description: "",
            shortDescription: "",
            type: "simple",
            status: "draft",
            price: 0,
            stock: 0,
            categoryId: "",
            images: [{ value: "" }],
        });
        setIsDialogOpen(true);
    };

    // Table Columns
    const columns: ColumnDef<Product>[] = [
        {
            accessorKey: "images",
            header: "Image",
            cell: ({ row }) => {
                const images = row.getValue("images") as (string | { url: string })[];
                const firstImage = images?.[0];
                // Handle both string URLs and object format { url }
                const imageUrl = typeof firstImage === 'string' ? firstImage : firstImage?.url;
                return (
                    <div className="h-10 w-10 rounded-md bg-muted overflow-hidden border">
                        {imageUrl ? (
                            <img src={imageUrl} alt={row.getValue("name")} className="h-full w-full object-cover" />
                        ) : (
                            <div className="flex h-full w-full items-center justify-center">
                                <ImageIcon className="h-4 w-4 text-muted-foreground" />
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            accessorKey: "name",
            header: "Product",
            cell: ({ row }) => (
                <div>
                    <div className="font-medium">{row.getValue("name")}</div>
                    <div className="text-xs text-muted-foreground">{row.original.sku || "No SKU"}</div>
                </div>
            ),
        },
        {
            accessorKey: "status",
            header: "Status",
            cell: ({ row }) => {
                const status = row.original.status || "draft";
                const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
                    active: "default",
                    draft: "secondary",
                    inactive: "outline",
                    out_of_stock: "destructive",
                };
                return <Badge variant={variants[status]}>{status.replace('_', ' ')}</Badge>;
            },
        },
        {
            accessorKey: "price",
            header: "Price",
            cell: ({ row }) => {
                const price = row.getValue("price") as number;
                return <div>${Number(price).toFixed(2)}</div>;
            },
        },
        {
            accessorKey: "stock",
            header: "Stock",
            cell: ({ row }) => (
                <div className={row.original.stock <= 0 ? "text-red-500 font-medium" : ""}>
                    {row.original.stock}
                </div>
            )
        },
        {
            accessorKey: "type",
            header: "Type",
            cell: ({ row }) => <span className="capitalize text-muted-foreground">{row.original.type}</span>
        },
        {
            id: "actions",
            cell: ({ row }) => {
                const product = row.original;
                return (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                                <MoreHorizontal className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEdit(product)}>
                                <Pencil className="mr-2 h-4 w-4" /> Edit Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(product.id)} className="text-destructive">
                                <Trash2 className="mr-2 h-4 w-4" /> Delete
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                );
            },
        },
    ];

    const table = useReactTable({
        data: products,
        columns,
        getCoreRowModel: getCoreRowModel(),
        onColumnFiltersChange: setColumnFilters,
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        state: { columnFilters },
    });

    return (
        <div className="space-y-6 h-full flex flex-col">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Products</h1>
                    <p className="text-muted-foreground">Manage your product catalog</p>
                </div>
                <Sheet open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                    <SheetContent side="right" className="w-[800px] sm:w-[540px] overflow-y-auto">
                        <SheetHeader>
                            <SheetTitle>{editingProduct ? "Edit Product" : "New Product"}</SheetTitle>
                            <SheetDescription>
                                {editingProduct ? `Editing ${editingProduct.name}` : "Create a new product definition."}
                            </SheetDescription>
                        </SheetHeader>

                        <div className="mt-6">
                            <Form {...form}>
                                <form onSubmit={form.handleSubmit(onSubmit, onFormError)} className="space-y-6">
                                    <Tabs defaultValue="general" className="w-full">
                                        <TabsList className={`grid w-full ${watchedType === 'variable' ? 'grid-cols-5' : 'grid-cols-4'}`}>
                                            <TabsTrigger value="general">General</TabsTrigger>
                                            <TabsTrigger value="pricing">Pricing</TabsTrigger>
                                            <TabsTrigger value="inventory">Inventory</TabsTrigger>
                                            {watchedType === 'variable' && <TabsTrigger value="variants">Variants</TabsTrigger>}
                                            <TabsTrigger value="media">Media</TabsTrigger>
                                        </TabsList>

                                        {/* === GENERAL TAB === */}
                                        <TabsContent value="general" className="space-y-4 mt-4">
                                            <div className="grid grid-cols-2 gap-4">
                                                <FormField control={form.control} name="name" render={({ field }) => (
                                                    <FormItem className="col-span-2">
                                                        <FormLabel>Product Name</FormLabel>
                                                        <FormControl><Input placeholder="Summer T-Shirt" {...field} /></FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )} />
                                                <FormField control={form.control} name="type" render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Type</FormLabel>
                                                        <Select onValueChange={field.onChange} value={field.value}>
                                                            <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                                                            <SelectContent>
                                                                <SelectItem value="simple">Simple Product</SelectItem>
                                                                <SelectItem value="variable">Variable Product</SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                    </FormItem>
                                                )} />
                                                <FormField control={form.control} name="status" render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Status</FormLabel>
                                                        <Select onValueChange={field.onChange} value={field.value}>
                                                            <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                                                            <SelectContent>
                                                                <SelectItem value="draft">Draft</SelectItem>
                                                                <SelectItem value="active">Active</SelectItem>
                                                                <SelectItem value="inactive">Inactive</SelectItem>
                                                                <SelectItem value="out_of_stock">Out of Stock</SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                    </FormItem>
                                                )} />
                                                <FormField control={form.control} name="categoryId" render={({ field }) => (
                                                    <FormItem className="col-span-2">
                                                        <FormLabel>Category</FormLabel>
                                                        <Select onValueChange={field.onChange} value={field.value}>
                                                            <FormControl><SelectTrigger><SelectValue placeholder="Select Category" /></SelectTrigger></FormControl>
                                                            <SelectContent>
                                                                {categories.map((cat) => (
                                                                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                        <FormMessage />
                                                    </FormItem>
                                                )} />
                                                <FormField control={form.control} name="description" render={({ field }) => (
                                                    <FormItem className="col-span-2">
                                                        <FormLabel>Description</FormLabel>
                                                        <FormControl><Textarea className="min-h-[100px]" placeholder="Product details..." {...field} /></FormControl>
                                                    </FormItem>
                                                )} />
                                            </div>
                                        </TabsContent>

                                        {/* === PRICING TAB === */}
                                        <TabsContent value="pricing" className="space-y-4 mt-4">
                                            <div className="grid grid-cols-2 gap-4">
                                                <FormField control={form.control} name="price" render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Regular Price ($)</FormLabel>
                                                        <FormControl><Input type="number" step="0.01" {...field} /></FormControl>
                                                    </FormItem>
                                                )} />
                                                <FormField control={form.control} name="salePrice" render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Sale Price ($)</FormLabel>
                                                        <FormControl><Input type="number" step="0.01" {...field} /></FormControl>
                                                    </FormItem>
                                                )} />
                                                <FormField control={form.control} name="costPrice" render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Cost per Item ($)</FormLabel>
                                                        <FormControl><Input type="number" step="0.01" {...field} /></FormControl>
                                                        <FormMessage className="text-xs text-muted-foreground">Internal use only</FormMessage>
                                                    </FormItem>
                                                )} />
                                            </div>
                                        </TabsContent>

                                        {/* === INVENTORY TAB === */}
                                        <TabsContent value="inventory" className="space-y-4 mt-4">
                                            <div className="grid grid-cols-2 gap-4">
                                                <FormField control={form.control} name="sku" render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>SKU (Stock Keeping Unit)</FormLabel>
                                                        <FormControl><Input placeholder="E.g. SUMMER-TSHIRT-001" {...field} /></FormControl>
                                                    </FormItem>
                                                )} />
                                                <FormField control={form.control} name="barcode" render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Barcode / ISBN</FormLabel>
                                                        <FormControl><Input placeholder="Scan code" {...field} /></FormControl>
                                                    </FormItem>
                                                )} />
                                                <FormField control={form.control} name="stock" render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Quantity in Stock</FormLabel>
                                                        <FormControl><Input type="number" {...field} /></FormControl>
                                                    </FormItem>
                                                )} />
                                                <FormField control={form.control} name="weight" render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Weight (kg)</FormLabel>
                                                        <FormControl><Input type="number" step="0.001" {...field} /></FormControl>
                                                    </FormItem>
                                                )} />
                                            </div>
                                        </TabsContent>

                                        {/* === VARIANTS TAB (for Variable Products) === */}
                                        {watchedType === 'variable' && (
                                            <TabsContent value="variants" className="space-y-4 mt-4">
                                                <div className="border border-dashed border-amber-500/50 bg-amber-500/10 rounded-lg p-6 text-center">
                                                    <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-4" />
                                                    <h3 className="text-lg font-semibold text-amber-600">Variant Management Coming Soon</h3>
                                                    <p className="text-sm text-muted-foreground mt-2">
                                                        Variable product support with multiple variants (sizes, colors, etc.) is under development.
                                                    </p>
                                                    <p className="text-sm text-muted-foreground mt-1">
                                                        For now, please use <strong>Simple Product</strong> and create separate products for each variant.
                                                    </p>
                                                </div>
                                            </TabsContent>
                                        )}

                                        {/* === MEDIA TAB === */}
                                        <TabsContent value="media" className="space-y-4 mt-4">
                                            <div className="border border-dashed rounded-lg p-6 flex flex-col items-center justify-center text-center">
                                                {uploadError && (
                                                    <div className="mb-4 w-full p-3 bg-red-500/10 border border-red-500/30 rounded-md text-red-500 text-sm">
                                                        <AlertTriangle className="h-4 w-4 inline mr-2" />
                                                        {uploadError}
                                                    </div>
                                                )}
                                                <Upload className="h-8 w-8 text-muted-foreground mb-4" />
                                                <h3 className="text-sm font-semibold">Upload Product Images</h3>
                                                <p className="text-xs text-muted-foreground mb-4">Drag and drop or click to upload</p>
                                                <Input
                                                    type="file"
                                                    multiple
                                                    accept="image/*"
                                                    className="hidden"
                                                    id="file-upload"
                                                    onChange={handleFileUpload}
                                                    disabled={isUploading}
                                                />
                                                <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById('file-upload')?.click()} disabled={isUploading}>
                                                    {isUploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : "Select Files"}
                                                </Button>
                                            </div>

                                            <ScrollArea className="h-[300px] w-full border rounded-md p-4">
                                                <div className="grid grid-cols-3 gap-4">
                                                    {fields.map((field, index) => (
                                                        <div key={field.id} className="relative group aspect-square rounded-md overflow-hidden border bg-muted">
                                                            {form.getValues(`images.${index}.value`) ? (
                                                                <>
                                                                    <img
                                                                        src={form.getValues(`images.${index}.value`)}
                                                                        alt={`Image ${index + 1}`}
                                                                        className="object-cover w-full h-full"
                                                                    />
                                                                    <Button
                                                                        type="button"
                                                                        variant="destructive"
                                                                        size="icon"
                                                                        className="absolute top-2 right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                                                        onClick={() => remove(index)}
                                                                    >
                                                                        <Trash2 className="h-3 w-3" />
                                                                    </Button>
                                                                </>
                                                            ) : (
                                                                <div className="flex items-center justify-center h-full text-muted-foreground text-xs">
                                                                    Empty Slot
                                                                    <Button
                                                                        type="button"
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="absolute top-2 right-2 h-6 w-6"
                                                                        onClick={() => remove(index)}
                                                                    >
                                                                        <Trash2 className="h-3 w-3" />
                                                                    </Button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </ScrollArea>
                                        </TabsContent>
                                    </Tabs>

                                    <SheetFooter className="absolute bottom-0 left-0 w-full bg-background border-t p-4">
                                        <Button
                                            type="submit"
                                            disabled={form.formState.isSubmitting}
                                            className="w-full"
                                            onClick={() => console.log("Save button clicked manually")}
                                        >
                                            {form.formState.isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                            Save Changes
                                        </Button>
                                    </SheetFooter>
                                    <div className="h-16" /> {/* Spacer for fixed footer */}
                                </form>
                            </Form>
                        </div>
                    </SheetContent>
                </Sheet>
                <div className="hidden">
                    {/* Hidden Dialog Trigger to preserve button click logic if needed, but we used SheetTrigger above... wait, I used Sheet but kept Button outside. */}
                    <Button onClick={handleAddNew}>Add Product</Button>
                </div>
            </div>

            <div className="flex items-center justify-between gap-4">
                <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        placeholder="Search products..."
                        value={(table.getColumn("name")?.getFilterValue() as string) ?? ""}
                        onChange={(event) => table.getColumn("name")?.setFilterValue(event.target.value)}
                        className="pl-8"
                    />
                </div>
                <Button onClick={handleAddNew}>
                    <Plus className="mr-2 h-4 w-4" /> Add Product
                </Button>
            </div>

            <Card className="flex-1">
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <TableHead key={header.id}>
                                            {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                                        </TableHead>
                                    ))}
                                </TableRow>
                            ))}
                        </TableHeader>
                        <TableBody>
                            {table.getRowModel().rows?.length ? (
                                table.getRowModel().rows.map((row) => (
                                    <TableRow key={row.id} data-state={row.getIsSelected() && "selected"}>
                                        {row.getVisibleCells().map((cell) => (
                                            <TableCell key={cell.id}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={columns.length} className="h-24 text-center">
                                        {loading ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : "No products found."}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    );
}
